import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormLabel from '@mui/material/FormLabel';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import TabSaveButton from './TabSaveButton';

export default function PatientInfoForm(tabIndex) {
  const { control } = useFormContext();

  // 🔹 이 탭에서 저장할 필드 경로 한 번에 정의
  const FIELD_PATHS = [
    'groupId',
    // 기본 환자 정보
    'patientInitial',
    'gender',
    'birthDate',
    'age',
    'ageGroup',
    'weight',
    'height',
    'race',
    'deathDate',
    'patientId',
    // 태아/신생아/영아 이상반응 관련
    'gestationPeriodValue',
    'gestationPeriodUnit',
    'birthWeight',
    'birthWeeks',
    'miscarriageDate',
  ];

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h6" sx={{ mb: 2 }}>
        환자 정보
      </Typography>

      <Grid container direction="row" spacing={2}>
        {/* 1. 환자이름(머리글자) */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="patientInitial"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField
                label="환자이름 (머리글자로 작성, 예; 홍길동→ㅎㄱㄷ, HKD)"
                fullWidth
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </Grid>
        {/* 2. 성별 */}
        <Grid container alignItems="flex-end" size={{ xs: 12, md: 6 }}>
          <Grid>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              성별
            </FormLabel>

            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel value="male" control={<Radio />} label="남성" />
                  <FormControlLabel value="female" control={<Radio />} label="여성" />
                  <FormControlLabel value="other" control={<Radio />} label="기타" />
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            연령정보
          </Typography>
        </Grid>
        {/* 생년월일: 년/월/일 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="birthDate"
                defaultValue=""
                control={control}
                render={({ field: { value, onChange, ...rest } }) => (
                  <TextField
                    label="생년월일 (YYYYMMDD)"
                    placeholder="YYYYMMDD"
                    value={value ?? null}
                    onChange={(e) => {
                      const next = e.target.value.replace(/\D/g, '').slice(0, 8);
                      onChange(next);
                    }}
                    inputMode="numeric"
                    slotProps={{ htmlInput: { maxLength: 8 } }}
                    onPaste={(e) => {
                      const pasted = e.clipboardData.getData('text');
                      // 숫자 이외 포함 또는 8자리 초과는 붙여넣기 차단
                      if (/\D/.test(pasted) || pasted.replace(/\D/g, '').length > 8) {
                        e.preventDefault();
                      }
                    }}
                    fullWidth
                    {...rest}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* 연령(나이) 숫자 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="age"
            defaultValue=""
            control={control}
            render={({ field }) => <TextField label="연령(나이)" fullWidth {...field} value={field.value ?? ''} />}
          />
        </Grid>
        {/* 연령대 체크박스 */}
        <Grid size={{ xs: 12 }}>
          <Grid>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              연령대 (정확한 연령 정보가 없는 경우)
            </FormLabel>
          </Grid>
          <Grid>
            <Controller
              name="ageGroup"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel value="fetus" control={<Radio />} label="태아" />
                  <FormControlLabel value="d0_28" control={<Radio />} label="출생일~28일 미만" />
                  <FormControlLabel value="d28_m24" control={<Radio />} label="28일~24개월 미만" />
                  <FormControlLabel value="m24_y12" control={<Radio />} label="24개월~12세 미만" />
                  <FormControlLabel value="y12_y19" control={<Radio />} label="12세~19세 미만" />
                  <FormControlLabel value="y19_y65" control={<Radio />} label="19세~65세 미만" />
                  <FormControlLabel value="y65_plus" control={<Radio />} label="65세 이상" />
                </RadioGroup>
              )}
            />
          </Grid>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 4. 체중(kg) */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="weight"
            defaultValue=""
            control={control}
            render={({ field }) => <TextField label="체중(kg)" fullWidth {...field} value={field.value ?? ''} />}
          />
        </Grid>
        {/* 5. 신장(cm) */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            name="height"
            defaultValue=""
            control={control}
            render={({ field }) => <TextField label="신장(cm)" fullWidth {...field} value={field.value ?? ''} />}
          />
        </Grid>
        {/* 6. 인종정보 체크박스 */}
        <Grid size={{ xs: 6 }}>
          <FormLabel component="legend" sx={{ mb: 1 }}>
            인종정보
          </FormLabel>
          <Controller
            name="race"
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel value="korean" control={<Radio />} label="한국인" />
                <FormControlLabel value="asianNonKorean" control={<Radio />} label="한국계 외 아시아인" />
                <FormControlLabel value="white" control={<Radio />} label="백인" />
                <FormControlLabel value="black" control={<Radio />} label="흑인" />
              </RadioGroup>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        {/* 7. 사망일 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="deathDate"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <TextField
                label="사망일 (사망한 경우)"
                placeholder="YYYYMMDD"
                fullWidth
                {...field}
                value={field.value ?? ''}
                inputMode="numeric"
                slotProps={{ htmlInput: { maxLength: 8 } }}
              />
            )}
          />
        </Grid>
        {/* 8. 식별번호 */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Controller
            name="patientId"
            defaultValue=""
            control={control}
            render={({ field }) => (
              <TextField
                label="식별번호 (환자의 진료기록번호 및 자료원)"
                fullWidth
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>
        <Typography variant="h6" sx={{ mb: 2 }}>
          환자정보 – 이상반응이 태아/신생아/영아에게 발생한 경우
        </Typography>
        <Grid container spacing={2}>
          {/* 1. 임신기간 (숫자) + 단위 선택 */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="gestationPeriodValue"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="임신기간"
                  type="number"
                  fullWidth
                  inputMode="numeric"
                  slotProps={{ htmlInput: { min: 0 } }}
                  value={field.value ?? ''}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormLabel component="legend" sx={{ mb: 1 }}>
              임신기간 단위
            </FormLabel>
            <Controller
              name="gestationPeriodUnit"
              control={control}
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <FormControlLabel value="day" control={<Radio />} label="일" />
                  <FormControlLabel value="week" control={<Radio />} label="주" />
                  <FormControlLabel value="month" control={<Radio />} label="월" />
                  <FormControlLabel value="trimester" control={<Radio />} label="임신분기" />
                </RadioGroup>
              )}
            />
          </Grid>

          {/* 2. 출생정보 - 체중(Double, kg) */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name="birthWeight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="출생 시 체중 (kg)"
                  type="number"
                  fullWidth
                  inputMode="decimal"
                  slotProps={{ htmlInput: { step: '0.01', min: 0 } }}
                  value={field.value ?? ''}
                />
              )}
            />
          </Grid>

          {/* 2. 출생정보 - 출생 주수(Int) */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Controller
              name="birthWeeks"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="출생 주수"
                  type="number"
                  fullWidth
                  inputMode="numeric"
                  slotProps={{ htmlInput: { min: 0 } }}
                  value={field.value ?? ''}
                />
              )}
            />
          </Grid>

          {/* 3. 유산일 (문자열) */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="miscarriageDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="유산일 (유산한 경우)"
                  placeholder="YYYYMMDD"
                  fullWidth
                  value={field.value ?? ''}
                  inputMode="numeric"
                  slotProps={{ htmlInput: { maxLength: 8 } }}
                />
              )}
            />
          </Grid>

          {/* 저장 버튼 */}
          <Grid size={{ xs: 12 }}>
            <TabSaveButton fieldPaths={FIELD_PATHS} label="탭 저장" index={tabIndex} />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
